import { TFunction } from 'i18next';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { GeodataProcessType } from '../../../../../typings/api/skymap/rest/v1/.common';
import { isDefined } from '../../../utils/variables';
import { ProjectSpatialReferenceContext } from '../../state/project-reference-state';
import { reset } from '../../utils/styled-reset';
import { Button } from '../button/button';
import { InfoBox } from '../info-box/info-box';
import { Projection } from '../project-settings/projection';
import { GeodataList } from './geodata-list/geodata-list';
import { ManualUploads } from './manual-uploads/manual-uploads';

enum View {
  Geodata,
  ManualUpload,
}

export const translatedProcessType = (
  processType: GeodataProcessType,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: TFunction<any>,
): string => {
  switch (processType) {
    case 'ODM':
      return t('setupDialog.cards.odm.title', { ns: 'cloudProcessing' });
    case 'FULL_HIGH_QUALITY':
      return t('setupDialog.cards.premium.title', { ns: 'cloudProcessing' });
    case 'FULL':
      return t('setupDialog.cards.standard.title', { ns: 'cloudProcessing' });
    case 'ORTHO_ONLY':
      return t('setupDialog.cards.orthophoto.title', { ns: 'cloudProcessing' });
    case 'ORTHO_TIF':
      return t('geodataPage.processTypes.orthophotoFromTif', { ns: 'cloudProcessing' });
    case 'POINTCLOUD_ONLY':
      return t('setupDialog.cards.pointCloud.title', { ns: 'cloudProcessing' });
    case 'SIGNATURE':
      return t('setupDialog.cards.signature.title', { ns: 'cloudProcessing' });
    case 'PM':
      return t('setupDialog.cards.pm.title', { ns: 'cloudProcessing' });
    default:
      return processType;
  }
};

const Geodata = () => {
  const { t } = useTranslation();
  const [view, setView] = React.useState(View.Geodata);
  const { coordinateSystem } = React.useContext(ProjectSpatialReferenceContext);
  const [isCoordinateSystemSet, setIsCoordinateSystemSet] = useState(isDefined(coordinateSystem));

  if (!isCoordinateSystemSet) {
    return (
      <Component>
        <InfoBox bottomMargin={true} color="yellow" topMargin={true}>
          {t('geodataPage.coordinateSystemRequired.message', { ns: 'cloudProcessing' })}
        </InfoBox>
        <Projection />
        <Button
          color="primary"
          disabled={!coordinateSystem}
          variant="contained"
          width="fit-content"
          onClick={() => setIsCoordinateSystemSet(true)}
        >
          {t('confirm', { ns: 'common' })}
        </Button>
      </Component>
    );
  }

  return (
    <Component>
      <Header>
        <Title selected={view === View.Geodata} onClick={() => setView(View.Geodata)}>
          {t('geodataPage.tabs.processes', { ns: 'cloudProcessing' })}
        </Title>
        <Title selected={view === View.ManualUpload} onClick={() => setView(View.ManualUpload)}>
          {t('geodataPage.tabs.uploads', { ns: 'cloudProcessing' })}
        </Title>
        <Border />
      </Header>

      <Content>
        {view === View.Geodata && <GeodataList />}
        {view === View.ManualUpload && <ManualUploads />}
      </Content>
    </Component>
  );
};

const Component = styled.div`
  ${reset}

  display: flex;
  flex-direction: column;
  flex: 1;

  > button {
    margin-top: 2em;
    width: 10em;
  }
`;

const Content = styled.div`
  display: flex;
  flex: 1;
  gap: 2em;
  width: 100%;
  justify-content: space-evenly;
`;

const Title = styled.div<{ selected: boolean }>`
  font-weight: 500;
  line-height: 25px;
  padding-right: 0.5em;
  white-space: nowrap;
  border-bottom: 2px solid
    ${(props) => (props.selected ? props.theme.color.brand.dark : props.theme.color.gray.medium)};
  cursor: pointer;

  &:hover {
    background-color: ${(props) => props.theme.color.gray.lightest};
  }
`;

const Border = styled.div`
  width: 100%;
  border-bottom: 2px solid ${(props) => props.theme.color.gray.medium};
`;

const Header = styled.div`
  display: flex;
  width: 100%;
  margin-bottom: 20px;
`;

export { Geodata };
